import React, { useEffect } from 'react'
import { useFeatureFlagVariantKey, useFeatureFlagEnabled } from '@posthog/react'
import posthog from 'posthog-js'
import { get as getConfig } from '../../../config/index'
import { POSTHOG_FEATURE_FLAGS } from '../../PostHog/index'
import { FloatingCard, NewPip } from '../../OracleCommons'
import { useTranslatedText } from '../../OracleCommons/StaticTextContext'
import { useOracleImage } from '../../OracleCommons/useOracleImage'
import { DivinationFeatureEntry } from '../DivinationFeatureEntry/DivinationFeatureEntry'
import { useIsCompactLayout } from '../useIsCompactLayout'

interface DailyDivinationCardProps {
  onTarotStart: () => void
}

const DailyDivinationCardHeader: React.FC = () => {
  const heading = useTranslatedText({ keys: ['dailyDivinationCard', 'heading'] })
  const newBadge = useTranslatedText({ keys: ['dailyDivinationCard', 'newBadge'] })

  return (
    <>
      <h2 className="daily-divination-card__heading">{heading}</h2>
      <NewPip label={newBadge} className="daily-divination-card__new-pip" />
    </>
  )
}

export const DailyDivinationCard: React.FC<DailyDivinationCardProps> = ({ onTarotStart }) => {
  const isCompact = useIsCompactLayout()
  const tarotKingWands = useOracleImage('tarot_king_wands.png')
  const tarotStrength = useOracleImage('tarot_strength.png')
  const tarotQueenCups = useOracleImage('tarot_queen_cups.png')

  const tarotTitle = useTranslatedText({ keys: ['dailyDivinationCard', 'tarot', 'title'] })
  const tarotDescriptionFull = useTranslatedText({ keys: ['dailyDivinationCard', 'tarot', 'description'] })
  const tarotDescriptionShort = useTranslatedText({ keys: ['dailyDivinationCard', 'tarot', 'descriptionShort'] })
  const tarotButtonText = useTranslatedText({ keys: ['dailyDivinationCard', 'tarot', 'buttonText'] })

  const oracleCardsTitle = useTranslatedText({ keys: ['dailyDivinationCard', 'oracleCards', 'title'] })
  const oracleCardsDescriptionFull = useTranslatedText({ keys: ['dailyDivinationCard', 'oracleCards', 'description'] })
  const oracleCardsDescriptionShort = useTranslatedText({ keys: ['dailyDivinationCard', 'oracleCards', 'descriptionShort'] })
  const oracleCardsButtonText = useTranslatedText({ keys: ['dailyDivinationCard', 'oracleCards', 'buttonText'] })

  const horoscopeTitle = useTranslatedText({ keys: ['dailyDivinationCard', 'horoscope', 'title'] })
  const horoscopeDescriptionFull = useTranslatedText({ keys: ['dailyDivinationCard', 'horoscope', 'description'] })
  const horoscopeDescriptionShort = useTranslatedText({ keys: ['dailyDivinationCard', 'horoscope', 'descriptionShort'] })
  const horoscopeButtonText = useTranslatedText({ keys: ['dailyDivinationCard', 'horoscope', 'buttonText'] })

  const tarotDescription = isCompact ? tarotDescriptionShort : tarotDescriptionFull
  const oracleCardsDescription = isCompact
    ? oracleCardsDescriptionShort
    : oracleCardsDescriptionFull
  const horoscopeDescription = isCompact ? horoscopeDescriptionShort : horoscopeDescriptionFull

  const config = getConfig()
  const aiConfig = config?.features?.ai ?? {}

  const divinationEnabled = !!aiConfig.divination

  const tarotVariant = useFeatureFlagVariantKey(POSTHOG_FEATURE_FLAGS.AI_DIVINATION_TAROT)
  const oracleCardsFlagEnabled = useFeatureFlagEnabled(
    POSTHOG_FEATURE_FLAGS.AI_DIVINATION_ORACLE_CARDS,
  )
  const horoscopeFlagEnabled = useFeatureFlagEnabled(POSTHOG_FEATURE_FLAGS.AI_DIVINATION_HOROSCOPE)

  // Track experiment exposure for the tarot A/B test. Fires once when the
  // variant is determined — PostHog deduplicates to the first exposure per user.
  // Uses posthog.capture() directly instead of the event factory system because
  // experiment exposure is a cross-cutting concern, not a feature-specific event.
  useEffect(() => {
    if (tarotVariant && (tarotVariant === 'test' || tarotVariant === 'control')) {
      posthog.capture('experiment_exposure', {
        '$feature/ai-divination-tarot': tarotVariant,
        experiment: 'ai-divination-tarot',
      })
    }
  }, [tarotVariant])

  if (!divinationEnabled) {
    return null
  }

  const origin = config?.origin ?? ''
  const isLocalhost = origin.includes('localhost')
  const isStaging = origin.includes('www-stg-4b2c19')
  const platformBypass = isLocalhost || isStaging

  const showTarot = !!aiConfig.tarot && (platformBypass || tarotVariant === 'test')
  const showOracleCards = !!aiConfig.oracle && (platformBypass || !!oracleCardsFlagEnabled)
  // TODO: give Horoscope its own home-grown config flag when building it out
  const showHoroscope = !!aiConfig.oracle && (platformBypass || !!horoscopeFlagEnabled)

  if (!showTarot && !showOracleCards && !showHoroscope) {
    return null
  }

  return (
    <FloatingCard className="daily-divination-card">
      <DailyDivinationCardHeader />
      {showTarot && (
        <DivinationFeatureEntry
          featureName="tarot"
          style={{
            ...(tarotKingWands && { '--card-img-left': `url(${tarotQueenCups})` }),
            ...(tarotStrength && { '--card-img-center': `url(${tarotStrength})` }),
            ...(tarotQueenCups && { '--card-img-right': `url(${tarotKingWands})` }),
          } as React.CSSProperties}
          title={tarotTitle}
          description={tarotDescription}
          buttonText={tarotButtonText}
          buttonState="filled"
          onClick={() => onTarotStart()}
        />
      )}
      {showOracleCards && (
        <DivinationFeatureEntry
          featureName="oracleCards"
          title={oracleCardsTitle}
          description={oracleCardsDescription}
          buttonText={oracleCardsButtonText}
          buttonState="outlined"
          onClick={() => {}}
        />
      )}
      {showHoroscope && (
        <DivinationFeatureEntry
          featureName="horoscope"
          title={horoscopeTitle}
          description={horoscopeDescription}
          buttonText={horoscopeButtonText}
          buttonState="outlined"
          onClick={() => {}}
        />
      )}
    </FloatingCard>
  )
}
